/**
 * Trip CO2 Calculator — "Your trip in perspective" section.
 *
 * Reframes the trip's kg CO2e into a more relatable unit: how many
 * days of the average person's annual carbon footprint it represents.
 *
 * Reference: global per-capita emissions ≈ 4,700 kg CO2e/year
 * (Our World in Data, 2022 cohort). That implies ≈ 12.9 kg/day per
 * person on Earth. We use that as the divisor — global, not regional,
 * because the calculator is public and a global comparator avoids the
 * geographic-snobbery framing (an EU-only baseline would make most
 * users look "better than average" by construction).
 */

import React from 'react';
import { MODE_DISPLAY, type ConfigByMode, type TransportMode } from './modes';
import InfoTip from './InfoTip';
import { pillStyle, dotStyle, formatVehiclePill } from './tripPills';

// ─────────────────────────────────────────────────────────────────────────────
// Reference data
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Global per-capita emissions baseline (Our World in Data, 2022 cohort).
 * Exported so the chart's `MethodologyPanel` can render the perspective
 * math in the merged "How is this calculated?" panel without duplicating
 * the constants. `PerspectiveSection` remains the source of truth.
 */
export const GLOBAL_AVG_KG_PER_PERSON_PER_YEAR = 4700;
export const GLOBAL_AVG_KG_PER_PERSON_PER_DAY = GLOBAL_AVG_KG_PER_PERSON_PER_YEAR / 365;

// ─────────────────────────────────────────────────────────────────────────────
// Public API
// ─────────────────────────────────────────────────────────────────────────────

export interface PerspectiveSectionProps {
  /** The user's chosen mode — drives the trip-tag emoji + label. */
  mode: TransportMode;
  /**
   * The user's chosen mode-config. Needed to render the per-vehicle
   * detail clause ("with N passengers in a petrol car") so the user
   * sees *why* the per-passenger kg came out where it did.
   */
  config: ConfigByMode[TransportMode];
  /** Trip kg CO2e per passenger (already RT-doubled if applicable). */
  chosenKg: number;
  originName: string;
  destinationName: string;
  isRoundTrip: boolean;
}

const PerspectiveSection: React.FC<PerspectiveSectionProps> = ({
  mode,
  config,
  chosenKg,
  originName,
  destinationName,
  isRoundTrip,
}) => {
  const { emoji, label } = MODE_DISPLAY[mode];
  const days = Math.max(1, Math.round(chosenKg / GLOBAL_AVG_KG_PER_PERSON_PER_DAY));
  // For vehicle modes (car / van / motorcycle) the transport details
  // (occupancy, fuel) are non-trivial enough to deserve their own pill
  // alongside the route pill. Other modes pack mode + route + trip
  // type into a single pill — they have nothing more to show.
  const vehiclePill = formatVehiclePill(mode, config);

  return (
    <section
      aria-label="Trip in perspective"
      style={{
        // No marginTop: the parent `.trip-calc-results-row` owns the
        // vertical rhythm. Setting it here would inline-override the
        // wrapper's CSS reset and misalign this card's top edge
        // against the chart card next to it.
        padding: '1.25rem',
        background: '#fafafa',
        border: '1px solid #e2e8f0',
        borderRadius: 10,
        textAlign: 'center',
        // Flex column so when this card stretches to match the
        // taller chart card on desktop, the contents (title → pills
        // → "X kg" sentence → big number → caption) distribute around
        // the vertical centre rather than crowding the top. On mobile
        // the section's height is content-driven, so centering is a
        // no-op there.
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
      }}
    >
      <h2 style={{ fontSize: '1.1rem', margin: '0 0 0.75rem' }}>Putting in perspective</h2>

      {/* Pill row. Block-level flex with center justification so on
          narrow viewports the two pills wrap to a vertical stack
          (still centered) rather than overflowing the section. */}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 6,
          justifyContent: 'center',
        }}
      >
        {vehiclePill ? (
          <>
            <div style={pillStyle}>
              <span>
                {originName} ↔ {destinationName}
              </span>
              <span style={dotStyle}>·</span>
              <span>{isRoundTrip ? 'Round trip' : 'One way'}</span>
            </div>
            <div style={pillStyle}>
              <span>{vehiclePill.emoji}</span>
              <span>{vehiclePill.label}</span>
              <span style={dotStyle}>·</span>
              <span>{vehiclePill.occupancy}</span>
              <span style={dotStyle}>·</span>
              <span>{vehiclePill.fuel}</span>
            </div>
          </>
        ) : (
          <div style={pillStyle}>
            <span>{emoji}</span>
            <span>{label}</span>
            <span style={dotStyle}>·</span>
            <span>
              {originName} ↔ {destinationName}
            </span>
            <span style={dotStyle}>·</span>
            <span>{isRoundTrip ? 'Round trip' : 'One way'}</span>
          </div>
        )}
      </div>

      <p style={{ margin: '0.9rem 0 0.4rem', fontSize: 15, color: '#0f172a' }}>
        This trip emits <b>~{Math.round(chosenKg).toLocaleString()} kg CO₂e per passenger</b>, equivalent to:
      </p>

      {/* Block-level wrapper keeps the number on its own line; inner
          flex with `justify-content: center` centres the number + word
          horizontally without making the wrapper itself inline. */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'baseline',
          gap: 8,
        }}
      >
        <span style={{ fontSize: 32, fontWeight: 800, color: '#047857', letterSpacing: -0.5 }}>
          {days.toLocaleString()}
        </span>
        <span style={{ fontSize: 14, fontWeight: 600, color: '#0f172a' }}>
          {days === 1 ? 'day' : 'days'}
        </span>
      </div>
      <p
        style={{
          margin: '2px 0 0',
          fontSize: 12.5,
          color: '#475569',
          fontStyle: 'italic',
          lineHeight: 1.45,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          justifyContent: 'center',
        }}
      >
        <span>of the average daily carbon footprint of a person living on Earth.</span>
        <InfoTip
          ariaLabel="About the average daily carbon footprint"
          content={
            <>
              Based on global per-capita emissions of ~{GLOBAL_AVG_KG_PER_PERSON_PER_YEAR.toLocaleString()} kg
              CO₂e per year (Our World in Data, 2022), or ~{GLOBAL_AVG_KG_PER_PERSON_PER_DAY.toFixed(1)} kg per
              person per day. The full methodology lives in the chart's "How is this calculated?" panel.
            </>
          }
        />
      </p>
    </section>
  );
};

export default PerspectiveSection;
